import React, { MouseEventHandler } from 'react'
import styled from 'styled-components'

import { useIsHydrated } from '@hozana/hooks/useIsHydrated'
import { IMAGE_SIZES } from '@hozana/styling/image'
import { EVENT_ORIGIN, UTM } from '@hozana/tracking/constants'
import { GTM } from '@hozana/tracking/gtm'

import { Block, TBlockProps } from 'elements/layout/Block'
import { Div } from 'elements/layout/Div'
import { PreloadDiv } from 'elements/layout/PreloadDiv'
import { VariableHeightImage } from 'elements/media/VariableHeightImage'
import { Fade } from 'elements/motion/Fade'
import { EllipsisMultiLines } from 'elements/text/EllipsisMultiLines'
import { H } from 'elements/text/H'
import { Link } from 'elements/text/Link'

import { PAGE } from 'routes/constants'

import { CommunityActivityStatusLabels } from 'modules/community/components/CommunityActivityOrStatusLabel'
import { CommunityCheckExplanation } from 'modules/community/components/CommunityCheckExplanation'
import { CommunityDraftExplanation } from 'modules/community/components/CommunityDraftExplanation'
import { CommunityFavoriteLabel } from 'modules/community/components/CommunityFavoriteLabel'
import { CommunityMembers } from 'modules/community/components/CommunityMembers'
import { JoinButton } from 'modules/community/components/JoinButton'
import { COMMUNITY_STATUS } from 'modules/community/constants'
import { getUserRole, isCommunityPublished } from 'modules/community/functions'
import { useCommunity } from 'modules/community/hooks/useCommunity'
import type { TJoinButtonProps } from 'modules/community/types'
import { InviteButton } from 'modules/share/components/InviteButton'
import { SHARE_OBJECTS } from 'modules/share/constants'
import { UserCard } from 'modules/user/components/UserCard'
import { UserProfileLink } from 'modules/user/components/UserProfileLink'

const defaultImg = '/img/hozana_img_community_default.png'

type TStyledBlockProps = { shadow?: boolean } & TBlockProps

const StyledBlock = styled(Block)<TStyledBlockProps>`
  transition: all 0.3s ease-in-out;
  ${(props) =>
    props.shadow &&
    `
    &:hover, &:focus {
      box-shadow: 0 3px 8px 2px rgba(0,0,0, 0.2);
    }
  `};
  overflow: hidden;
`

export type TCommunityBlockProps = {
  admin?: boolean
  communityId: number
  hasFixedHeight?: boolean
  hideDescription?: boolean
  showCreator?: boolean
  trackingSource?: TJoinButtonProps['trackingSource']
  eventOrigin: EVENT_ORIGIN
  displayActions?: boolean
  isOwnProfile?: boolean
  onClick?: MouseEventHandler<HTMLButtonElement>
  onJoin?: MouseEventHandler<HTMLButtonElement>
  showIntroduction?: boolean
  /** If true, we cannot know if the user has joined or not */
  hasJoinNotUpToDate?: boolean
  isStatic?: boolean
} & TStyledBlockProps

/**
 * ### CommunityBlock ###
 */
const WrappedCommunityBlock: TFCC<TCommunityBlockProps> = ({
  admin,
  communityId,
  hasFixedHeight,
  hideDescription,
  showCreator = true,
  trackingSource,
  eventOrigin,
  borderRadius,
  onClick,
  onJoin,
  showIntroduction = true,
  hasJoinNotUpToDate,
  ...otherProps
}) => {
  const isHydrated = useIsHydrated()

  const community = useCommunity({ communityId })

  if (!community) {
    return null
  }

  const isDraft = community.status === COMMUNITY_STATUS.DRAFT
  const isCheck = community.status === COMMUNITY_STATUS.CHECK
  const communityPublished = isCommunityPublished(community)
  const { isMember, isCreator, hasRole } = getUserRole(community)

  const openAdminPage = hasRole && (admin || !communityPublished)

  return (
    <StyledBlock align="left" h="100%" borderRadius={borderRadius} showIntroduction={showIntroduction} {...otherProps}>
      <Link
        to={{
          pathname: openAdminPage ? PAGE.COMMUNITY_VIEW_ADMIN : PAGE.COMMUNITY_VIEW,
          query: { communityId: community.id, communitySlug: community.slug, origin: eventOrigin },
        }}
        reload={!openAdminPage}
        noFollow={community.isSeoIndexed === false}
        gtm={{ event: GTM.EVENTS.COMMUNAUTE_CLICKED, variables: { trackingSource } }}
        onClick={onClick}
      >
        <Fade opacity={isDraft ? '0.7' : '1'}>
          <VariableHeightImage
            src={community.picture || defaultImg}
            size={IMAGE_SIZES.community.medium}
            alt="trans:community:community.image-alt"
          />
        </Fade>

        <EllipsisMultiLines
          lines={hasFixedHeight ? '2' : '10'} // 10 is a random value
          p="0 16px"
          lineHeight="28px"
          fontSize="20px"
          color={isDraft ? 'black50' : 'black'}
          h={hasFixedHeight ? '3em' : undefined}
          as={H} // to add properties and tag of H
          size="3"
          m="8px 0"
        >
          {community.name}
        </EllipsisMultiLines>
        {!hideDescription && (
          <Div
            p="0 16px"
            h={hasFixedHeight ? '72px' : { xs: '5.6em', md: 'auto' }}
            maxH={{ md: '4.8em' }}
            noOverflow
            m="0 0 16px"
          >
            <EllipsisMultiLines color="black70" lines="3">
              <CommunityActivityStatusLabels community={community} m="0 5px 0 0" fontSize="15px" />
              <CommunityFavoriteLabel community={community} m="0 10px 0 0" fontSize="15px" />
              {community.introduction}
            </EllipsisMultiLines>
          </Div>
        )}
      </Link>
      {showCreator && (
        <Div p="0 1em">
          <UserProfileLink userId={community.user} w="100%">
            <UserCard userId={community.user}>trans:community:community.animated-by.short</UserCard>
          </UserProfileLink>
        </Div>
      )}
      <Div p="0 16px" m="8px 0 16px">
        {isDraft && <CommunityDraftExplanation community={community} shortText w="100%" />}
        {isCheck && <CommunityCheckExplanation shortText w="100%" />}
        {communityPublished && (
          <>
            <CommunityMembers community={community} openMembersList={isHydrated} m="0 8px 0 0" fontSize="15px" />
            {hasJoinNotUpToDate ? (
              <PreloadDiv h="38px" radius="small" />
            ) : isMember || isCreator ? (
              <InviteButton
                objectToShare={SHARE_OBJECTS.COMMUNITY}
                trackingSource={trackingSource}
                community={community}
                utmContent={UTM.CONTENT.COMMUNITY_BLOCK_INVITE_BUTTON}
                m="1em 0 0"
                w="100%"
              />
            ) : (
              <JoinButton
                community={community}
                trackingSource={trackingSource}
                eventOrigin={eventOrigin}
                onClick={onJoin}
                m="1em 0 0"
                w="100%"
              />
            )}
          </>
        )}
      </Div>
    </StyledBlock>
  )
}

export const CommunityBlock = React.memo(WrappedCommunityBlock)
